import { useCoastguard } from "@cofano/coastguard2-frontend-client";
import { useEffect, useState } from "react";
import { request } from "components/utils/Request2";
import { getFilePropsFromResponse, showFile } from "components/filepreview/FilePreview";
import { saveAs } from "file-saver";
import {  CircularProgress, Drawer, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { localeAsLanguageKey, locales } from "utils/locales";
import { Relation } from "view/relations/types/relation";
import { intl } from "LocaleWrapper";
import { Button } from "@cofano/cck";

type CustomerUpdatePdfDrawerProps = {
	open: boolean;
	onClose: () => void;
	onSend: () => void;
	relation: Relation;
}

const CustomerUpdatePdfDrawer = ({ open, onClose, onSend, relation }: CustomerUpdatePdfDrawerProps) => {
	const { instance } = useCoastguard();

	const [ loading, setLoading ] = useState(false);
	const [ downloading, setDownloading ] = useState(false);

	const [ pdfLanguage, setPdfLanguage ] = useState(relation.language?.code?.toLocaleLowerCase ?? "en");
	const [ response, setResponse ] = useState<unknown>(undefined);

	useEffect(() => {
		const url = `customerupdate/pdf/export/${relation.id}?preview=true&language=${pdfLanguage}`;

		if (open && relation.id) {
			setLoading(true);

			request({
				url: url,
				method: "GET",
			}).then(res => setResponse(res))
				.finally(() => setLoading(false));
		}
	}, [ open, pdfLanguage, relation.id ]);

	const fileProps = response ? getFilePropsFromResponse(response) : undefined;

	const onDownload = () => {
		const url = `/customerupdate/pdf/export/${relation.id}?language=${pdfLanguage}`;

		setDownloading(true);

		instance.fetchBlob(url, {
			method: "GET",
		}).then(async (blob) => {
			saveAs(blob, `customer-update-${relation.name}.pdf`);
		}).finally(() => {
			setDownloading(false);
		});
	};

	return (
		<Drawer anchor="right"
			open={open}
			onClose={onClose}
			style={{ zIndex: 1000 }}>
			<div style={{ padding: 16, width: "66vw", minWidth: 700, display: "flex", flexGrow: 1, flexDirection: "column", gap: 16 }}>
				<Grid container spacing={3}>
					<Grid size={8}>
						<RadioGroup row value={pdfLanguage} onChange={event => setPdfLanguage(event.target.value)}>
							{Object.values(locales).map((locale, index) => (
								<FormControlLabel
									key={index}
									value={locale}
									control={<Radio />}
									label={localeAsLanguageKey(locale)}
									disabled={loading || downloading} />
							))}
						</RadioGroup>
					</Grid>
					<Grid size={4} style={{ alignItems: "center", display: "flex", justifyContent: "end", gap: 8 }}>
						<Button
							onClick={onSend}
							disabled={downloading || loading}
							size="small">
							{intl.formatMessage({ id: "SEND" })}
						</Button>
						<Button
							onClick={onDownload}
							loading={downloading}
							disabled={downloading || loading}
							size="small"
							variant="outlined">
							{intl.formatMessage({ id: "DOWNLOAD" })}
						</Button>
					</Grid>
				</Grid>

				{loading || !fileProps ? (
					<CircularProgress size={24} />
				) : (
					<div style={{ display: "flex", flexGrow: 1 }}>
						{showFile(fileProps.file, fileProps.type)}
					</div>
				)}
			</div>
		</Drawer>
	);
};

export default CustomerUpdatePdfDrawer;
